import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';

const AdminActionPlanPage = () => {
  const { user } = useUser();
  const [activeTasks, setActiveTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSemester, setSelectedSemester] = useState(() => {
    return localStorage.getItem('selectedSemester') || '';
  });
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showEditDatesModal, setShowEditDatesModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [staffByTeam, setStaffByTeam] = useState({});
  const [filters, setFilters] = useState({
    team: 'ALL',
    status: 'ALL',
    triggerEvent: 'ALL',
    ddayRange: 'ALL'
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'deadline',
    direction: 'asc'
  });

  // 권한 체크 헬퍼 함수
  const canManageActionPlan = () => {
    if (!user || !user.role) return false;
    const role = user.role.toUpperCase();
    return ['LEADER', 'PRESIDENT', 'ADMIN'].includes(role);
  };
  const [generateFormData, setGenerateFormData] = useState({
    semester: '',
    branch: '',
    semesterStart: '',
    expoStart: '',
    meet1: '',
    meet2: '',
    meet3: '',
    outing: '',
    meet1Determined: false,
    meet2Determined: false,
    meet3Determined: false,
    outingDetermined: false
  });
  const [editDatesData, setEditDatesData] = useState({
    semesterStart: '',
    expoStart: '',
    meet1: '',
    meet2: '',
    meet3: '',
    outing: ''
  });
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('TODO');
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 학기 옵션 생성 (최근 3년)
  const generateSemesterOptions = () => {
    const options = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear - 1; year <= currentYear + 1; year++) {
      options.push(`${year}-Spring`);
      options.push(`${year}-Fall`);
    }
    
    return options;
  };

  const semesterOptions = generateSemesterOptions();

  useEffect(() => {
    if (selectedSemester) {
      localStorage.setItem('selectedSemester', selectedSemester);
      fetchActiveTasks();
    } else {
      localStorage.removeItem('selectedSemester');
    }
  }, [selectedSemester]);

  useEffect(() => {
    fetchTemplates();
    fetchAvailableSemesters();
  }, []);

  // availableSemesters가 로드된 후, localStorage의 selectedSemester 검증
  // 단, API 에러로 빈 배열이 된 경우는 제외 (데이터가 실제로 있을 수 있음)
  useEffect(() => {
    if (availableSemesters.length > 0 && selectedSemester) {
      // localStorage에서 복원된 값이 실제 목록에 없으면 초기화
      if (!availableSemesters.includes(selectedSemester)) {
        setSelectedSemester('');
      }
    }
    // 주의: availableSemesters가 빈 배열이어도 selectedSemester를 초기화하지 않음
    // (API 에러로 인한 빈 배열일 수 있으므로)
  }, [availableSemesters]);

  // 필터링과 정렬 적용
  useEffect(() => {
    let filtered = [...activeTasks];

    // 팀 필터링
    if (filters.team !== 'ALL') {
      filtered = filtered.filter(task => task.team === filters.team);
    }

    // 상태 필터링
    if (filters.status !== 'ALL') {
      filtered = filtered.filter(task => task.status === filters.status);
    }

    // Trigger Event 필터링
    if (filters.triggerEvent !== 'ALL') {
      filtered = filtered.filter(task => task.triggerEvent === filters.triggerEvent);
    }

    // D-day 필터링
    if (filters.ddayRange !== 'ALL') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(task => {
        const taskDate = new Date(task.deadline);
        taskDate.setHours(0, 0, 0, 0);
        const diffTime = taskDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        switch (filters.ddayRange) {
          case 'OVERDUE':
            return diffDays < 0;
          case 'TODAY':
            return diffDays === 0;
          case 'THIS_WEEK':
            return diffDays > 0 && diffDays <= 7;
          case 'NEXT_WEEK':
            return diffDays > 7 && diffDays <= 14;
          case 'THIS_MONTH':
            return diffDays > 14 && diffDays <= 30;
          case 'FUTURE':
            return diffDays > 30;
          default:
            return true;
        }
      });
    }


    // 정렬
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'deadline') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredTasks(filtered);
  }, [activeTasks, filters, sortConfig]);

  const fetchActiveTasks = async () => {
    if (!selectedSemester) return;
    
    try {
      setLoading(true);
      // "2024-Fall (NCCU)" 형식에서 학기명과 브랜치 추출
      const [semester, branch] = selectedSemester.includes('(') 
        ? selectedSemester.split(' (')
        : [selectedSemester, ''];
      const cleanBranch = branch.replace(')', '');
      
      const response = await axios.get(`/api/action-plans?semester=${semester}`);
      // 브랜치별로 필터링
      const filteredTasks = response.data.filter(task => task.branch === cleanBranch);
      setActiveTasks(filteredTasks);
      
      // 각 팀별 staff 데이터 가져오기 (항상 새로 fetch)
      const teams = ['GA', 'PR', 'EP', 'Collective'];
      for (const team of teams) {
        await fetchStaffByTeam(team);
      }
    } catch (error) {
      alert('Failed to load Action Plan list.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/templates');
      setTemplates(response.data);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchStaffByTeam = async (team) => {
    try {
      const response = await axios.get(`/api/staff/team/${team}`);
      setStaffByTeam(prev => ({
        ...prev,
        [team]: response.data
      }));
    } catch (error) {
      // 에러 발생 시에도 빈 배열로 설정하여 드롭다운이 비어있음을 명시
      setStaffByTeam(prev => ({
        ...prev,
        [team]: []
      }));
    }
  };

  const fetchAvailableSemesters = async () => {
    try {
      const response = await axios.get('/api/action-plans?semester=all');
      const semesters = [...new Set(response.data.map(task => `${task.semester} (${task.branch})`))].sort();
      setAvailableSemesters(semesters);
    } catch (error) {
      setAvailableSemesters([]);
    }
  };

  const handleTemplateSelect = (templateId, isSelected) => {
    if (isSelected) {
      setSelectedTemplates(prev => [...prev, templateId]);
    } else {
      setSelectedTemplates(prev => prev.filter(id => id !== templateId));
    }
  };

  const handleSelectAllTemplates = (isSelected) => {
    if (isSelected) {
      setSelectedTemplates(templates.map(t => t.id));
    } else {
      setSelectedTemplates([]);
    }
  };

  const handleResetSemester = async () => {
    if (!selectedSemester) return;
    
    try {
      // "2024-Fall (NCCU)" 형식에서 학기명과 브랜치 추출
      const [semester, branch] = selectedSemester.includes('(') 
        ? selectedSemester.split(' (')
        : [selectedSemester, ''];
      const cleanBranch = branch.replace(')', '');
      
      // 해당 학기+브랜치의 모든 tasks 삭제
      const response = await axios.get(`/api/action-plans?semester=${semester}`);
      const tasksToDelete = response.data.filter(task => task.branch === cleanBranch);
      
      for (const task of tasksToDelete) {
        await axios.delete(`/api/action-plans/${task.id}`);
      }
      
      alert(`Successfully deleted ${tasksToDelete.length} tasks for ${selectedSemester}`);
      
      // 목록을 먼저 새로고침
      await fetchAvailableSemesters();
      
      // 선택 해제 및 활성 태스크 초기화
      setActiveTasks([]);
      setSelectedSemester('');
    } catch (error) {
      alert('Failed to reset semester: ' + (error.response?.data?.message || error.message));
    } finally {
      setShowResetModal(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`/api/action-plans/${taskId}`, { status: newStatus });
      
      // 로컬 상태 업데이트
      setActiveTasks(prev => 
        prev.map(task => 
          task.id === taskId ? { ...task, status: newStatus } : task
        )
      );
      
      alert('Status updated.');
    } catch (error) {
      alert('Failed to update status.');
    }
  };

  const handleStaffAssignment = async (taskId, assignedStaff) => {
    try {
      await axios.put(`/api/action-plans/${taskId}`, { assignedStaff });
      
      // 로컬 상태 업데이트
      setActiveTasks(prev => 
        prev.map(task => 
          task.id === taskId ? { ...task, assignedStaff } : task
        )
      );
      
      alert('Staff assignment updated.');
    } catch (error) {
      alert('Failed to update staff assignment.');
    }
  };

  const handleGenerateActionPlan = async (e) => {
    e.preventDefault();
    
    if (selectedTemplates.length === 0) {
      alert('Please select at least one template.');
      return;
    }
    
    try {
      const requestData = {
        ...generateFormData,
        selectedTemplates
      };
      
      const response = await axios.post('/api/action-plans/generate', requestData);
      
      if (response.data.success) {
        alert(`Action Plan generated successfully. (${response.data.taskCount} tasks)`);
        
        // 생성된 학기+브랜치 값을 미리 저장 (초기화하기 전에!)
        const newSemesterKey = `${generateFormData.semester} (${generateFormData.branch})`;
        
        setShowGenerateModal(false);
        setGenerateFormData({
          semester: '',
          branch: '',
          semesterStart: '',
          expoStart: '',
          meet1: '',
          meet2: '',
          meet3: '',
          outing: '',
          meet1Determined: false,
          meet2Determined: false,
          meet3Determined: false,
          outingDetermined: false
        });
        setSelectedTemplates([]);
        
        // 목록 새로고침 후 생성된 학기+브랜치로 자동 선택
        await fetchAvailableSemesters();
        setSelectedSemester(newSemesterKey);
      }
    } catch (error) {
      alert('Failed to generate action plan: ' + (error.response?.data?.message || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'TODO':
        return 'bg-gray-100 text-gray-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'DONE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'TODO':
        return 'To-Do';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'DONE':
        return 'Done';
      default:
        return status;
    }
  };

  const getTeamColor = (team) => {
    switch (team) {
      case 'GA':
        return 'bg-green-100 text-green-800';
      case 'PR':
        return 'bg-blue-100 text-blue-800';
      case 'EP':
        return 'bg-purple-100 text-purple-800';
      case 'Collective':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  const getDaysUntilDeadline = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleEditDates = () => {
    // 현재 선택된 학기의 이벤트 날짜들을 가져와서 모달에 표시
    // 실제로는 백엔드에서 이벤트 날짜를 가져와야 하지만, 
    // 현재는 빈 값으로 시작하고 사용자가 입력하도록 함
    setEditDatesData({
      semesterStart: '',
      expoStart: '',
      meet1: '',
      meet2: '',
      meet3: '',
      outing: ''
    });
    setShowEditDatesModal(true);
  };

  const handleUpdateDates = async () => {
    try {
      // 백엔드 API 호출하여 이벤트 날짜 업데이트
      // 실제 구현에서는 백엔드에 이벤트 날짜 업데이트 API가 필요
      // 임시로 성공 메시지 표시
      alert('Event dates updated successfully!');
      setShowEditDatesModal(false);
      
      // Action Plan 새로고침
      if (selectedSemester) {
        fetchActiveTasks();
      }
    } catch (error) {
      alert('Failed to update event dates.');
    }
  };

  const handleTaskSelect = (taskId, isSelected) => {
    if (isSelected) {
      setSelectedTasks(prev => [...prev, taskId]);
    } else {
      setSelectedTasks(prev => prev.filter(id => id !== taskId));
    }
  };

  const handleSelectAllTasks = (isSelected) => {
    if (isSelected) {
      setSelectedTasks(filteredTasks.map(task => task.id));
    } else {
      setSelectedTasks([]);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedTasks.length === 0) {
      alert('Please select at least one task.');
      return;
    }

    try {
      // 선택된 모든 작업의 상태를 일괄 업데이트
      const updatePromises = selectedTasks.map(taskId => 
        axios.put(`/api/action-plans/${taskId}`, { status: bulkStatus })
      );
      
      await Promise.all(updatePromises);
      
      // 로컬 상태 업데이트
      setActiveTasks(prev => 
        prev.map(task => 
          selectedTasks.includes(task.id) ? { ...task, status: bulkStatus } : task
        )
      );
      
      alert(`Successfully updated ${selectedTasks.length} tasks to ${bulkStatus}.`);
      setSelectedTasks([]);
    } catch (error) {
      alert('Failed to update task statuses.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Action Plan Management</h1>
          {canManageActionPlan() && (
            <button
              onClick={() => setShowGenerateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm text-sm sm:text-base w-full sm:w-auto"
            >
              Create Action Plan
            </button>
          )}
        </div>

        {/* 학기+브랜치 선택 */}
        {availableSemesters.length > 0 && (
          <div className="mb-6 p-4 sm:p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-base sm:text-lg font-medium text-blue-900 mb-3">Available Action Plans</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableSemesters.map((semesterKey) => (
                <button
                  key={semesterKey}
                  onClick={() => setSelectedSemester(semesterKey)}
                  className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-colors w-full ${
                    selectedSemester === semesterKey
                      ? 'border-blue-500 bg-blue-100 text-blue-900'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="font-medium text-sm sm:text-base truncate">{semesterKey}</div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">Click to view tasks</div>
                </button>
              ))}
            </div>
            {selectedSemester && (
              <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pt-4 border-t border-blue-200">
                <div className="w-full sm:w-auto">
                  <span className="text-xs sm:text-sm text-blue-700 font-medium">Selected: </span>
                  <span className="text-xs sm:text-sm text-blue-900 font-semibold break-all sm:break-normal">{selectedSemester}</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <button
                    onClick={handleEditDates}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs sm:text-sm font-medium w-full sm:w-auto"
                  >
                    Edit Event Dates
                  </button>
                  {canManageActionPlan() && (
                    <button
                      onClick={() => setShowResetModal(true)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-xs sm:text-sm font-medium w-full sm:w-auto"
                    >
                      Reset Selected
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Plan 테이블 */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {!selectedSemester ? (
            <div className="p-8 text-center">
              <div className="text-gray-500 mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
        </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Action Plan Selected</h3>
              <p className="text-gray-500 mb-4">Select an existing action plan above or click "Create Action Plan" to create a new one</p>
            </div>
          ) : (
            <>
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="text-lg">Loading...</div>
              </div>
              ) : (
                <>
                  <div className="px-6 py-4 bg-gray-50 border-b">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <h2 className="text-lg font-medium text-gray-900">
                        {selectedSemester} Action Plan ({filteredTasks.length} tasks)
                      </h2>
                      
                      {/* 필터링 옵션 및 일괄 업데이트 */}
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Team</label>
                            <select
                              value={filters.team}
                              onChange={(e) => handleFilterChange('team', e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="ALL">All Teams</option>
                              <option value="GA">GA</option>
                              <option value="PR">PR</option>
                              <option value="EP">EP</option>
                              <option value="Collective">Collective</option>
                            </select>
                          </div>
              <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                            <select
                              value={filters.status}
                              onChange={(e) => handleFilterChange('status', e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="ALL">All Status</option>
                              <option value="TODO">To-Do</option>
                              <option value="IN_PROGRESS">In Progress</option>
                              <option value="DONE">Done</option>
                            </select>
              </div>
              <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Trigger Event</label>
                            <select
                              value={filters.triggerEvent}
                              onChange={(e) => handleFilterChange('triggerEvent', e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="ALL">All Events</option>
                              <option value="SemesterStart">Semester Start</option>
                              <option value="ExpoDay">Expo Day</option>
                              <option value="MeetDay">Meet Day</option>
                              <option value="Outing">Outing</option>
                </select>
              </div>
              <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">D-day</label>
                            <select
                              value={filters.ddayRange}
                              onChange={(e) => handleFilterChange('ddayRange', e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="ALL">All D-days</option>
                              <option value="OVERDUE">Overdue</option>
                              <option value="TODAY">Today</option>
                              <option value="THIS_WEEK">This Week</option>
                              <option value="NEXT_WEEK">Next Week</option>
                              <option value="THIS_MONTH">This Month</option>
                              <option value="FUTURE">Future</option>
                            </select>
                          </div>
                        </div>
                        
                        {/* Bulk Update - 항상 표시 */}
                        <div className="flex items-end gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Bulk Update</label>
                            <select
                              value={bulkStatus}
                              onChange={(e) => setBulkStatus(e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="TODO">To-Do</option>
                              <option value="IN_PROGRESS">In Progress</option>
                              <option value="DONE">Done</option>
                            </select>
                          </div>
                          <button
                            onClick={handleBulkStatusUpdate}
                            disabled={selectedTasks.length === 0}
                            className={`px-3 py-1 text-xs rounded ${
                              selectedTasks.length === 0 
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            Update {selectedTasks.length} tasks
                          </button>
                        </div>
                      </div>
        </div>
                  </div>
                
                {/* 모바일 반응형: 카드 뷰 vs 테이블 뷰 */}
                {isMobile ? (
                  /* Mobile Card View */
                  <div className="p-4 space-y-4">
                    {/* Select All Checkbox */}
                    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                      <input
                        type="checkbox"
                        checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0}
                        onChange={(e) => handleSelectAllTasks(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Select All ({selectedTasks.length} selected)
                      </label>
                    </div>
                    
                    {filteredTasks.map((task) => {
                      const daysUntilDeadline = getDaysUntilDeadline(task.deadline);
                      return (
                        <div 
                          key={task.id} 
                          className={`bg-gray-50 p-4 rounded-lg border border-gray-200 ${selectedTasks.includes(task.id) ? 'border-blue-500 bg-blue-50' : ''}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start gap-3 flex-1">
                              <input
                                type="checkbox"
                                checked={selectedTasks.includes(task.id)}
                                onChange={(e) => handleTaskSelect(task.id, e.target.checked)}
                                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <h3 className="text-base font-semibold text-gray-900 mb-2">{task.taskName}</h3>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTeamColor(task.team)}`}>
                                    {task.team}
                                  </span>
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                    {task.triggerEvent || 'N/A'}
                                  </span>
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(task.status)}`}>
                                    {getStatusDisplayName(task.status)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Deadline</p>
                              <p className="font-medium text-gray-900">{formatDate(task.deadline)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">D-day</p>
                              <p className={`font-medium ${
                                daysUntilDeadline < 0 ? 'text-red-600' :
                                daysUntilDeadline <= 7 ? 'text-orange-600' :
                                'text-gray-600'
                              }`}>
                                {daysUntilDeadline < 0 ? `D+${Math.abs(daysUntilDeadline)}` :
                                 daysUntilDeadline === 0 ? 'D-day' :
                                 `D-${daysUntilDeadline}`}
                              </p>
                            </div>
                          </div>
                          
                          <div className="pt-3 border-t border-gray-200 space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                              <select
                                value={task.status}
                                onChange={(e) => handleStatusChange(task.id, e.target.value)}
                                className={`w-full px-3 py-2 text-sm font-semibold rounded-lg border-0 focus:ring-2 focus:ring-blue-500 ${getStatusColor(task.status)}`}
                              >
                                <option value="TODO">To-Do</option>
                                <option value="IN_PROGRESS">In Progress</option>
                                <option value="DONE">Done</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Assigned Staff</label>
                              <select
                                value={task.assignedStaff || 'TEAM'}
                                onChange={(e) => handleStaffAssignment(task.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="TEAM">Team</option>
                                {staffByTeam[task.team]?.map(staff => (
                                  <option key={staff.id} value={staff.name}>
                                    {staff.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Desktop Table View */
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0}
                              onChange={(e) => handleSelectAllTasks(e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('taskName')}
                          >
                            Task Name {sortConfig.key === 'taskName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('team')}
                          >
                            Team {sortConfig.key === 'team' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('triggerEvent')}
                          >
                            Trigger Event {sortConfig.key === 'triggerEvent' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('deadline')}
                          >
                            Deadline {sortConfig.key === 'deadline' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            D-day
                          </th>
                          <th 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('status')}
                          >
                            Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Assigned Staff
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTasks.map((task) => {
                          const daysUntilDeadline = getDaysUntilDeadline(task.deadline);
                          return (
                            <tr key={task.id} className={selectedTasks.includes(task.id) ? 'bg-blue-50' : ''}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <input
                                  type="checkbox"
                                  checked={selectedTasks.includes(task.id)}
                                  onChange={(e) => handleTaskSelect(task.id, e.target.checked)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {task.taskName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTeamColor(task.team)}`}>
                                  {task.team}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                  {task.triggerEvent || 'N/A'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(task.deadline)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <span className={`font-medium ${
                                  daysUntilDeadline < 0 ? 'text-red-600' :
                                  daysUntilDeadline <= 7 ? 'text-orange-600' :
                                  'text-gray-600'
                                }`}>
                                  {daysUntilDeadline < 0 ? `D+${Math.abs(daysUntilDeadline)}` :
                                   daysUntilDeadline === 0 ? 'D-day' :
                                   `D-${daysUntilDeadline}`}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <select
                                  value={task.status}
                                  onChange={(e) => handleStatusChange(task.id, e.target.value)}
                                  className={`px-2 py-1 text-xs font-semibold rounded-full border-0 focus:ring-2 focus:ring-blue-500 ${getStatusColor(task.status)}`}
                                >
                                  <option value="TODO">To-Do</option>
                                  <option value="IN_PROGRESS">In Progress</option>
                                  <option value="DONE">Done</option>
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <select
                                  value={task.assignedStaff || 'TEAM'}
                                  onChange={(e) => handleStaffAssignment(task.id, e.target.value)}
                                  className="w-full min-w-[120px] px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="TEAM">Team</option>
                                  {staffByTeam[task.team]?.map(staff => (
                                    <option key={staff.id} value={staff.name}>
                                      {staff.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                  
                  {filteredTasks.length === 0 && activeTasks.length > 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No tasks found with the selected filters.
        </div>
      )}

                  {activeTasks.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No Action Plan found for this semester. Click "Start New Semester" to generate one.
                    </div>
                  )}
                </>
              )}
            </>
                  )}
              </div>

        {/* Action Plan 생성 Modal */}
        {showGenerateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Create New Action Plan
                </h3>
                
                <form onSubmit={handleGenerateActionPlan} className="space-y-6">
                  {/* Step 1: Basic Info */}
                  <div className="border-b pb-4">
                    <h4 className="text-md font-medium text-gray-800 mb-3">Step 1: Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Semester
                        </label>
                        <input
                          type="text"
                          value={generateFormData.semester}
                          onChange={(e) => setGenerateFormData({...generateFormData, semester: e.target.value})}
                          placeholder="2024-FALL"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Format: YYYY-SPRING or YYYY-FALL (e.g., 2024-FALL)
                        </p>
              </div>
              <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Branch
                        </label>
                        <select
                          value={generateFormData.branch}
                          onChange={(e) => setGenerateFormData({...generateFormData, branch: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Branch</option>
                  <option value="NCCU">NCCU</option>
                  <option value="NTU">NTU</option>
                  <option value="TAIPEI">TAIPEI</option>
                </select>
              </div>
            </div>
                  </div>

                  {/* Step 2: Event Dates */}
                  <div className="border-b pb-4">
                    <h4 className="text-md font-medium text-gray-800 mb-3">Step 2: Event Dates</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Semester Start Date
                        </label>
                        <input
                          type="date"
                          value={generateFormData.semesterStart}
                          onChange={(e) => setGenerateFormData({...generateFormData, semesterStart: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expo Start Date
                        </label>
                        <input
                          type="date"
                          value={generateFormData.expoStart}
                          onChange={(e) => setGenerateFormData({...generateFormData, expoStart: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>
                    
            <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Meet Dates</h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="flex items-center mb-1">
                            <input
                              type="checkbox"
                              checked={generateFormData.meet1Determined}
                              onChange={(e) => setGenerateFormData({...generateFormData, meet1Determined: e.target.checked, meet1: e.target.checked ? generateFormData.meet1 : ''})}
                              className="mr-2"
                            />
                            <label className="text-sm font-medium text-gray-700">Meet 1 Date</label>
                          </div>
                          <input
                            type="date"
                            value={generateFormData.meet1}
                            onChange={(e) => setGenerateFormData({...generateFormData, meet1: e.target.value})}
                            disabled={!generateFormData.meet1Determined}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          />
                        </div>
                        <div>
                          <div className="flex items-center mb-1">
                            <input
                              type="checkbox"
                              checked={generateFormData.meet2Determined}
                              onChange={(e) => setGenerateFormData({...generateFormData, meet2Determined: e.target.checked, meet2: e.target.checked ? generateFormData.meet2 : ''})}
                              className="mr-2"
                            />
                            <label className="text-sm font-medium text-gray-700">Meet 2 Date</label>
                          </div>
                          <input
                            type="date"
                            value={generateFormData.meet2}
                            onChange={(e) => setGenerateFormData({...generateFormData, meet2: e.target.value})}
                            disabled={!generateFormData.meet2Determined}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          />
                        </div>
                        <div>
                          <div className="flex items-center mb-1">
                            <input
                              type="checkbox"
                              checked={generateFormData.meet3Determined}
                              onChange={(e) => setGenerateFormData({...generateFormData, meet3Determined: e.target.checked, meet3: e.target.checked ? generateFormData.meet3 : ''})}
                              className="mr-2"
                            />
                            <label className="text-sm font-medium text-gray-700">Meet 3 Date</label>
            </div>
                          <input
                            type="date"
                            value={generateFormData.meet3}
                            onChange={(e) => setGenerateFormData({...generateFormData, meet3: e.target.value})}
                            disabled={!generateFormData.meet3Determined}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          />
            </div>
          </div>
        </div>

                    <div className="mt-4">
                      <div className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          checked={generateFormData.outingDetermined}
                          onChange={(e) => setGenerateFormData({...generateFormData, outingDetermined: e.target.checked, outing: e.target.checked ? generateFormData.outing : ''})}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700">Outing Date</label>
                      </div>
                      <input
                        type="date"
                        value={generateFormData.outing}
                        onChange={(e) => setGenerateFormData({...generateFormData, outing: e.target.value})}
                        disabled={!generateFormData.outingDetermined}
                        className="w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>

                  {/* Step 3: Template Selection */}
                  <div className="border-b pb-4">
                    <h4 className="text-md font-medium text-gray-800 mb-3">Step 3: Template Selection</h4>
                    <div className="mb-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedTemplates.length === templates.length && templates.length > 0}
                          onChange={(e) => handleSelectAllTemplates(e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Select All Templates ({selectedTemplates.length} selected of {templates.length} total)
                        </span>
                      </label>
                    </div>
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                      {templates.map((template) => (
                        <div key={template.id} className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={selectedTemplates.includes(template.id)}
                            onChange={(e) => handleTemplateSelect(template.id, e.target.checked)}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">{template.taskName}</span>
                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              template.team === 'GA' ? 'bg-green-100 text-green-800' :
                              template.team === 'PR' ? 'bg-blue-100 text-blue-800' :
                              template.team === 'EP' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {template.team}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowGenerateModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm"
                    >
                      Generate Action Plan
                    </button>
                </div>
                </form>
              </div>
            </div>
        </div>
        )}

        {/* Edit Event Dates Modal */}
        {showEditDatesModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Event Dates</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Update event dates for <strong>{selectedSemester}</strong>. This will recalculate all task deadlines.
                </p>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Semester Start Date</label>
                      <input
                        type="date"
                        value={editDatesData.semesterStart}
                        onChange={(e) => setEditDatesData(prev => ({ ...prev, semesterStart: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expo Start Date</label>
                      <input
                        type="date"
                        value={editDatesData.expoStart}
                        onChange={(e) => setEditDatesData(prev => ({ ...prev, expoStart: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Meet Dates</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Meet 1 Date</label>
                        <input
                          type="date"
                          value={editDatesData.meet1}
                          onChange={(e) => setEditDatesData(prev => ({ ...prev, meet1: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Meet 2 Date</label>
                        <input
                          type="date"
                          value={editDatesData.meet2}
                          onChange={(e) => setEditDatesData(prev => ({ ...prev, meet2: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Meet 3 Date</label>
                        <input
                          type="date"
                          value={editDatesData.meet3}
                          onChange={(e) => setEditDatesData(prev => ({ ...prev, meet3: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Outing Date</label>
                    <input
                      type="date"
                      value={editDatesData.outing}
                      onChange={(e) => setEditDatesData(prev => ({ ...prev, outing: e.target.value }))}
                      className="w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowEditDatesModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateDates}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                  >
                    Update Dates
                  </button>
                </div>
              </div>
            </div>
        </div>
        )}

        {/* Reset Confirmation Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reset Action Plan</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to reset the selected action plan? This will permanently delete all tasks for <strong>{selectedSemester}</strong>.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowResetModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
              <button
                    onClick={handleResetSemester}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                  >
                    Reset
              </button>
                </div>
        </div>
      </div>
        </div>
      )}
    </div>
  );
};

export default AdminActionPlanPage;